import { useEffect, useRef, useState } from 'react'
import { isNavigatorOnline } from '@/shared/lib/network'

const RESTORED_TOAST_MS = 3500
const NETWORK_TOAST_SCROLL_PADDING = '3rem'

export const NetworkStatusToast = () => {
  const [isOnline, setIsOnline] = useState(() => isNavigatorOnline())
  const [showRestored, setShowRestored] = useState(false)
  const timeoutRef = useRef<number | null>(null)
  const prevScrollPaddingRef = useRef<string | null>(null)

  const visible = !isOnline || showRestored

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (visible) {
      prevScrollPaddingRef.current = document.documentElement.style.scrollPaddingTop
      document.documentElement.style.scrollPaddingTop = NETWORK_TOAST_SCROLL_PADDING
      return () => {
        document.documentElement.style.scrollPaddingTop = prevScrollPaddingRef.current ?? ''
        prevScrollPaddingRef.current = null
      }
    }
    return undefined
  }, [visible])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleOffline = () => {
      setIsOnline(false)
      setShowRestored(false)
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    const handleOnline = () => {
      setIsOnline(true)
      setShowRestored(true)
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = window.setTimeout(() => {
        setShowRestored(false)
        timeoutRef.current = null
      }, RESTORED_TOAST_MS)
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!visible) return null

  return (
    <div
      role="status"
      aria-live={isOnline ? 'polite' : 'assertive'}
      className="fixed left-1/2 top-3 z-50 -translate-x-1/2 px-3"
    >
      <div
        className={`rounded-xl border px-3 py-2 text-xs font-semibold shadow-[0_10px_24px_rgba(31,43,40,0.14)] ${
          isOnline
            ? 'border-[var(--color-mint-strong)] bg-[var(--color-mint-soft)] text-[var(--color-text-primary)]'
            : 'border-[var(--color-wine-soft-border)] bg-[var(--color-wine-soft)] text-[var(--color-wine-strong)]'
        }`}
      >
        {isOnline
          ? 'Conexão restabelecida. Reenviando alterações pendentes...'
          : 'Sem conexão. Alterações serão reenviadas automaticamente ao reconectar.'}
      </div>
    </div>
  )
}
